// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Button,
  Grid,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import PieChart from "examples/Charts/PieChart";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import usePageTitle from "app/pageTitle";
import GoogleMapReact from "google-map-react";
import AppConfig from "appConfig";

function DawLeaderboardPage() {
  const pageTitle = "Display & Win - Leaderboard";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({});

  const [mapZoom, setMapZoom] = useState(13);

  const tableColumns = [
    { Header: "Rank", accessor: "rank", align: "left" },
    { Header: "Retailer", accessor: "retailer", align: "left" },
    { Header: "Points", accessor: "points", align: "left" },
    { Header: "Visits", accessor: "visits", align: "left" },
  ];

  const buildRateRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        rank: row.rank,
        retailer: row.retailer,
        points: row.points,
        visits: row.visits,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/daw/${ref}/leaderboard.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setStats(result.data.data.stats);
            const rows = buildRateRows(result.data.data.leaderboard);
            setLeaderboard(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  function getPieChartColors(data) {
    const colors = ["info", "primary", "secondary", "error", "warning", "success"];
    const bgColors = [];
    data.every(() => {
      bgColors.push(colors[Math.floor(Math.random() * colors.length)]);
      return true;
    });

    return bgColors;
  }

  function getPieChartLabels(data) {
    const labels = [];
    data.every((row) => {
      labels.push(row.label);
      return true;
    });

    return labels;
  }

  function getPieChartData(data) {
    const datas = [];
    data.every((row) => {
      datas.push(row.count);
      return true;
    });

    return datas;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mb={5}>
        <MDBox mt={1} mb={2} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/trade-marketing/promotions/display-win/${ref}`);
              }}
            >
              Back
            </Button>
          </MDBox>
        </MDBox>
        {pageLoaded && (
          <MDBox>
            <MDBox></MDBox>
            <MDBox mt={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8} lg={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={6}>
                      <Card>
                        <MDBox p={2} align="center">
                          <MDTypography variant="h6" fontSize={24}>
                            <Icon>storefront</Icon>
                          </MDTypography>
                          <MDTypography mt={2} variant="h6" fontSize={36}>
                            {stats.retailers.registered}
                          </MDTypography>
                          <MDTypography mt={2} variant="subtitle2" fontSize={14}>
                            Registered
                            <br />
                            Retailers
                          </MDTypography>
                        </MDBox>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <Card>
                        <MDBox p={2} align="center">
                          <MDTypography variant="h6" fontSize={24}>
                            <Icon>store</Icon>
                          </MDTypography>
                          <MDTypography mt={2} variant="h6" fontSize={36}>
                            {stats.retailers.active}
                          </MDTypography>
                          <MDTypography mt={2} variant="subtitle2" fontSize={14}>
                            Active
                            <br />
                            Retailers
                          </MDTypography>
                        </MDBox>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <Card>
                        <MDBox p={2} align="center">
                          <MDTypography variant="h6" fontSize={24}>
                            <Icon>location_on</Icon>
                          </MDTypography>
                          <MDTypography mt={2} variant="h6" fontSize={36}>
                            {stats.visits.visits}
                          </MDTypography>
                          <MDTypography mt={1.5} variant="subtitle2" fontSize={14}>
                            Total
                            <br />
                            Visits
                          </MDTypography>
                        </MDBox>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <Card>
                        <MDBox p={2} align="center">
                          <MDTypography variant="h6" fontSize={24}>
                            <Icon>loyalty</Icon>
                          </MDTypography>
                          <MDTypography mt={2} variant="h6" fontSize={36}>
                            {stats.visits.points}
                          </MDTypography>
                          <MDTypography mt={1.5} variant="subtitle2" fontSize={14}>
                            Total
                            <br />
                            Points
                          </MDTypography>
                        </MDBox>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4} lg={4}>
                  <MDBox sx={{ height: "100%" }}>
                    <PieChart
                      sx={{ marginTop: "0px" }}
                      // icon={{ color: "info", component: "leaderboard" }}
                      title="Products Breakdown"
                      description=""
                      chart={{
                        labels: getPieChartLabels(stats.products),
                        datasets: {
                          label: "Products Breakdown",
                          backgroundColors: getPieChartColors(stats.products),
                          data: getPieChartData(stats.products),
                        },
                      }}
                    />
                  </MDBox>
                </Grid>
                {/* {stats.products.map((product) => (
                  <Grid item xx={6} md={4} lg={4}>
                    <Card>
                      <MDBox p={2} align="center">
                        <MDTypography variant="h6" fontSize={16}>
                          {product.label}
                        </MDTypography>
                        <MDTypography mt={2} variant="h6" fontSize={24}>
                          {product.count}
                        </MDTypography>
                        <MDTypography mt={2} variant="subtitle2" fontSize={14}>
                          Displayed Quantity
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                ))} */}
              </Grid>
            </MDBox>
            {stats.retailers.retailerSpreadData.length > 0 && (
              <MDBox mt={5}>
                <Card>
                  <MDBox p={3}>
                    <MDBox mb={3} display="flex" justifyContent="space-between">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Retailer HeatMap
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom -= 1;
                            pZoom = pZoom < 4 ? 4 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>remove</Icon>
                        </MDButton>
                        &nbsp;
                        <MDTypography variant="caption" fontSize={10} fontWeight="light">
                          Zoom
                        </MDTypography>
                        &nbsp;
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom += 1;
                            pZoom = pZoom > 20 ? 20 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox sx={{ height: "65vh", width: "100%" }}>
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: AppConfig.google_map_key }}
                        defaultCenter={{
                          lat: stats.retailers.retailerSpreadData[0].latitude,
                          lng: stats.retailers.retailerSpreadData[0].longitude,
                        }}
                        defaultZoom={mapZoom}
                        zoom={mapZoom}
                      >
                        {stats.retailers.retailerSpreadData.map((loc) => (
                          <MDBox lat={loc.latitude} lng={loc.longitude}>
                            <MDTypography fontSize={24}>
                              <Icon color="error">location_on</Icon>
                            </MDTypography>
                          </MDBox>
                        ))}
                      </GoogleMapReact>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}

            <MDBox mt={5}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={6}>
                  <PieChart
                    sx={{ height: "100%", marginTop: "0px" }}
                    // icon={{ color: "info", component: "leaderboard" }}
                    title="States Breakdown"
                    description=""
                    chart={{
                      labels: getPieChartLabels(stats.retailers.statesStats),
                      datasets: {
                        label: "States Breakdown",
                        backgroundColors: getPieChartColors(stats.retailers.statesStats),
                        data: getPieChartData(stats.retailers.statesStats),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <PieChart
                    sx={{ height: "100%", marginTop: "0px" }}
                    // icon={{ color: "info", component: "leaderboard" }}
                    title="LGAs Breakdown"
                    description=""
                    chart={{
                      labels: getPieChartLabels(stats.retailers.lgaStats),
                      datasets: {
                        label: "LGAs Breakdown",
                        backgroundColors: getPieChartColors(stats.retailers.lgaStats),
                        data: getPieChartData(stats.retailers.lgaStats),
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </MDBox>
            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={0}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox p={2}>
                      <MDTypography variant="h6" fontWeight="medium">
                        Retailer Leaderboard
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDBox mt={1}>
                    <DataTable
                      table={{ columns: tableColumns, rows: leaderboard }}
                      isSorted
                      entriesPerPage
                      showTotalEntries
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DawLeaderboardPage;
